// Intent: Napper's screenshot trick — when the user screenshots the app,
// gently offer sharing it with their referral code (viral loop, 50 zł talon
// both ways). Native only (expo-screen-capture; web no-op), max once a day,
// NEVER on the vault (a PESEL screenshot is not a marketing moment).

import { usePathname } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, Share, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { fetchReferralInfoApi } from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { useAppMetaStore } from '@/store/app-meta-store';
import { useAuthStore } from '@/store/auth-store';
import { colors, radius, shadows, spacing } from '@/theme/tokens';

const APP_URL = 'https://opiekun-rodziny.expo.app';

export function ShareToast() {
  const pathname = usePathname();
  const account = useAuthStore((s) => s.account);
  const shareToastDay = useAppMetaStore((s) => s.shareToastDay);
  const setShareToastDay = useAppMetaStore((s) => s.setShareToastDay);
  const [visible, setVisible] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  // Latest pathname for the listener without re-subscribing.
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useEffect(() => {
    if (Platform.OS === 'web') return;
    let sub: { remove: () => void } | undefined;
    (async () => {
      try {
        const ScreenCapture = await import('expo-screen-capture');
        sub = ScreenCapture.addScreenshotListener(() => {
          const today = new Date().toISOString().slice(0, 10);
          // once a day; never on the vault (privacy)
          if (useAppMetaStore.getState().shareToastDay === today) return;
          if (pathRef.current.includes('vault')) return;
          void useAppMetaStore.getState().setShareToastDay(today);
          setVisible(true);
        });
      } catch {
        // screenshot detection unavailable — feature silently off
      }
    })();
    return () => sub?.remove();
    // mount once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!visible || !account) return;
    let cancelled = false;
    (async () => {
      const res = await fetchReferralInfoApi();
      if (!cancelled && res.ok && res.data) setCode(res.data.code);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, account]);

  if (!visible) return null;

  const message = code
    ? `Polecam darmową aplikację Opiekun Rodziny: ${APP_URL} • Wpisz mój kod ${code} — oboje dostaniemy talon 50 zł.`
    : `Polecam darmową aplikację Opiekun Rodziny: ${APP_URL}`;

  return (
    <View
      style={{
        position: 'absolute',
        left: spacing.screen,
        right: spacing.screen,
        bottom: 96,
        backgroundColor: colors.navy,
        borderRadius: radius.card,
        padding: spacing.md,
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        ...shadows.card,
      }}
    >
      <Ionicons name="camera-outline" size={22} color="#7CC4F0" />
      <View style={{ flex: 1, gap: 2 }}>
        <AppText variant="labelLg" color="#FFFFFF">
          Podoba się? Podziel się!
        </AppText>
        <AppText variant="labelMd" color="#DCE9FF">
          {code ? `Twój kod ${code} = talon 50 zł dla Was obojga.` : 'Wyślij znajomym link do aplikacji.'}
        </AppText>
      </View>
      <Pressable
        accessibilityRole="button"
        onPress={async () => {
          setVisible(false);
          try {
            await Share.share({ message });
          } catch {
            // cancelled — fine
          }
        }}
        style={{ backgroundColor: colors.primary, borderRadius: radius.full, paddingHorizontal: spacing.md, paddingVertical: spacing.xs, minHeight: 36, justifyContent: 'center' }}
      >
        <AppText variant="labelLg" color="#FFFFFF">
          Udostępnij
        </AppText>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Zamknij"
        onPress={() => setVisible(false)}
        style={{ minWidth: 32, minHeight: 36, alignItems: 'center', justifyContent: 'center' }}
      >
        <Ionicons name="close" size={18} color="#DCE9FF" />
      </Pressable>
    </View>
  );
}
